import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogIn, LogOut, RotateCcw, ClipboardList, FileText, ShieldCheck, FileDown, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtPrice } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { useQuote } from "@/context/QuoteContext";
import { SectionTag } from "@/components/Section";

const STATUS_STYLE = { new: "bg-[#E63946] text-white", contacted: "bg-amber-100 text-amber-800", quoted: "bg-blue-100 text-blue-800", fulfilled: "bg-emerald-100 text-emerald-800", closed: "bg-slate-200 text-slate-600" };
const TYPE_LABEL = { quote: "Quote request", contact: "Inquiry", sample: "Sample request", supplier: "Supplier application" };

export default function Account() {
  const { user, loading, login, logout } = useAuth();
  const { add, setOpen } = useQuote();
  const location = useLocation();
  const [list, setList] = useState(null);

  useEffect(() => {
    if (user) api.get("/account/inquiries").then((r) => setList(r.data)).catch(() => setList([]));
  }, [user]);

  if (loading) return <div className="py-32 text-center text-slate-400" data-testid="account-loading">Checking session…</div>;

  if (!user) return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="account-login-page">
      <div className="sr-card p-10">
        <img src="/logo.jpg" alt="ShelfReady AI" className="h-10 mx-auto" />
        <p className="sr-label mt-8">Buyer account</p>
        <h1 className="mt-2 text-2xl font-extrabold uppercase text-[#0B1F4B]">Your quotes, in one place</h1>
        <p className="mt-3 text-sm text-slate-500">Sign in with Google to see your quote history, priced quotes from our team, and reorder past lists in one click.</p>
        {location.state?.authError && <p className="mt-3 text-sm text-[#E63946]" data-testid="account-auth-error">Sign-in failed. Please try again.</p>}
        <button onClick={() => login("/account")} className="sr-btn-blue mt-6 w-full justify-center" data-testid="account-login-button"><LogIn size={16} /> Sign in with Google</button>
        <p className="mt-4 text-xs text-slate-400">Quotes submitted with your email address are linked automatically.</p>
      </div>
    </div>
  );

  const reorder = (inq) => {
    const src = inq.quote?.items?.length ? inq.quote.items : inq.items;
    src.forEach((i) => add({ product_id: i.product_id, name: i.name, size: i.size, brand_name: i.brand_name, case_pack: i.case_pack, image: null }, i.quantity));
    toast.success(`${src.length} SKUs added to your quote list`);
    setOpen(true);
  };

  const accept = async (inq) => {
    if (!window.confirm(`Accept quote ${inq.inquiry_id} for ${fmtPrice(inq.quote.total)}? Our team will be notified and will confirm your order.`)) return;
    try {
      const { data } = await api.post(`/account/inquiries/${inq.inquiry_id}/accept`);
      setList(list.map((x) => (x.inquiry_id === data.inquiry_id ? data : x)));
      toast.success("Quote accepted — our team has been notified and will confirm your order.");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not accept quote"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10" data-testid="account-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <SectionTag>Buyer account</SectionTag>
          <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold uppercase text-[#0B1F4B]">Welcome back, {user.name?.split(" ")[0] || "buyer"}</h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="account-user-email">{user.email}</p>
        </div>
        <div className="flex items-center gap-2">
          {user.role === "admin" && <Link to="/admin" className="sr-btn-outline !py-2 !px-4 text-xs" data-testid="account-admin-link"><ShieldCheck size={14} /> Admin</Link>}
          <Link to="/catalog" className="sr-btn-blue !py-2 !px-4 text-xs" data-testid="account-new-quote"><ClipboardList size={14} /> New quote</Link>
          <button onClick={logout} className="sr-btn-outline !py-2 !px-4 text-xs" data-testid="account-logout-button"><LogOut size={14} /> Sign out</button>
        </div>
      </div>

      {list === null && <p className="mt-10 text-slate-400">Loading your history…</p>}
      {list?.length === 0 && (
        <div className="sr-card p-12 mt-10 text-center text-slate-500" data-testid="account-empty">
          <FileText className="mx-auto mb-3 text-[#1E7CF2]" size={32} />
          No quote requests yet. Build a list from the <Link to="/catalog" className="text-[#1E7CF2] underline">catalog</Link> and submit it with this email to see it here.
        </div>
      )}
      <div className="mt-8 space-y-4">
        {list?.map((inq) => (
          <div key={inq.inquiry_id} className="sr-card p-6" data-testid={`account-inquiry-${inq.inquiry_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-slate-500">{inq.inquiry_id}</span>
                  <span className="text-[10px] uppercase tracking-wider font-bold rounded-full bg-[#EBF3FE] text-[#1E40AF] px-2 py-0.5">{TYPE_LABEL[inq.type]}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${STATUS_STYLE[inq.status]}`} data-testid={`account-status-${inq.inquiry_id}`}>{inq.status}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{new Date(inq.created_at).toLocaleString()}{inq.company ? ` · ${inq.company}` : ""}</p>
              </div>
              {(inq.items?.length > 0 || inq.quote?.items?.length > 0) && (
                <button onClick={() => reorder(inq)} className="sr-btn-primary !py-2 !px-4 text-xs" data-testid={`account-reorder-${inq.inquiry_id}`}><RotateCcw size={14} /> Reorder this list</button>
              )}
            </div>

            {inq.quote ? (
              <div className="mt-4 rounded-xl border border-[#D6E6FB] overflow-hidden" data-testid={`account-quote-${inq.inquiry_id}`}>
                <div className="bg-[#EBF3FE] px-4 py-2 flex justify-between items-center text-xs">
                  <span className="font-bold uppercase tracking-wider text-[#1E40AF]">Priced quote · sent {new Date(inq.quote.sent_at).toLocaleDateString()}</span>
                  {inq.quote.valid_until && <span className="text-slate-600">Valid until {inq.quote.valid_until}</span>}
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {inq.quote.items.map((x) => (
                      <tr key={x.product_id} className="border-t border-slate-100">
                        <td className="px-4 py-2 text-slate-500 text-xs">{x.brand_name}</td>
                        <td className="px-2 py-2 font-semibold text-[#0B1F4B]">{x.name} <span className="text-slate-400 font-normal text-xs">{x.size}</span></td>
                        <td className="px-2 py-2 text-right font-mono text-xs">{x.quantity} cs × {fmtPrice(x.unit_price)}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold">{fmtPrice(x.line_total)}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-slate-200 text-xs text-slate-500"><td colSpan={3} className="px-4 py-1.5 text-right">Subtotal</td><td className="px-4 py-1.5 text-right font-mono">{fmtPrice(inq.quote.subtotal)}</td></tr>
                    <tr className="text-xs text-slate-500"><td colSpan={3} className="px-4 py-1.5 text-right">Freight</td><td className="px-4 py-1.5 text-right font-mono">{fmtPrice(inq.quote.freight)}</td></tr>
                    <tr className="bg-[#EBF3FE]"><td colSpan={3} className="px-4 py-2 text-right font-bold uppercase text-xs text-[#0B1F4B]">Quote total</td><td className="px-4 py-2 text-right font-mono font-extrabold text-[#0B1F4B]" data-testid={`account-quote-total-${inq.inquiry_id}`}>{fmtPrice(inq.quote.total)}</td></tr>
                  </tbody>
                </table>
                {inq.quote.notes && <p className="px-4 py-3 text-sm text-slate-600 border-t border-slate-100 whitespace-pre-wrap">{inq.quote.notes}</p>}
                <div className="px-4 py-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                  {inq.quote.accepted_at ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700" data-testid={`account-quote-accepted-${inq.inquiry_id}`}><CheckCircle2 size={16} /> Accepted on {new Date(inq.quote.accepted_at).toLocaleDateString()} — our team is confirming your order</span>
                  ) : (
                    <button onClick={() => accept(inq)} className="sr-btn-blue !py-2 !px-4 text-xs" data-testid={`account-accept-quote-${inq.inquiry_id}`}><CheckCircle2 size={14} /> Accept quote · {fmtPrice(inq.quote.total)}</button>
                  )}
                  <button onClick={() => downloadBlob(`/inquiries/${inq.inquiry_id}/quote.pdf`, `ShelfReady-Quote-${inq.inquiry_id}.pdf`)} className="sr-btn-outline !py-2 !px-4 text-xs" data-testid={`account-quote-pdf-${inq.inquiry_id}`}><FileDown size={14} /> Download quote PDF</button>
                </div>
                {!inq.quote.accepted_at && <p className="px-4 pb-3 text-[11px] text-slate-400">Accepting notifies our team; final order confirmation, payment and freight are arranged directly with you.</p>}
              </div>
            ) : inq.items?.length > 0 ? (
              <ul className="mt-4 divide-y divide-slate-100 text-sm">
                {inq.items.map((x) => <li key={x.product_id} className="py-1.5 flex justify-between"><span><span className="text-slate-500 text-xs mr-2">{x.brand_name}</span>{x.name} <span className="text-slate-400 text-xs">{x.size}</span></span><span className="font-mono text-xs">{x.quantity} cs</span></li>)}
              </ul>
            ) : inq.message ? <p className="mt-3 text-sm text-slate-600 whitespace-pre-wrap">{inq.message}</p> : null}
          </div>
        ))}
      </div>
    </div>
  );
}
